import { Link, type LinkComponentProps } from '@tanstack/react-router';
import clsx from 'clsx';
import type { ComponentProps, ReactNode } from 'react';

// Pill-button van donar.nl: gekleurde achtergrond met een witte "ripple" die
// bij hover vanuit het midden uitvouwt.

type ButtonColor = 'primary' | 'secondary';
type ButtonSize = 'xs' | 'sm' | 'md';

interface ButtonStyleProps {
  color?: ButtonColor;
  size?: ButtonSize;
}

const colorClasses: Record<ButtonColor, { background: string; content: string }> = {
  primary: {
    background: 'bg-orange-500',
    content: 'group-hover/button:text-orange-500',
  },
  secondary: {
    background: 'bg-blue-500',
    content: 'group-hover/button:text-blue-500',
  },
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'gap-1 px-3 py-1.5 text-xs',
  sm: 'gap-2 px-3 py-1.5 text-sm',
  md: 'gap-3.5 px-7 py-3.5 text-md',
};

const baseClasses =
  'group/button relative inline-block overflow-hidden rounded-full no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60 disabled:grayscale';

function ButtonLayers({
  color = 'primary',
  size = 'md',
  children,
}: ButtonStyleProps & { children: ReactNode }) {
  return (
    <>
      <span
        className={clsx(
          'absolute inset-0 overflow-hidden rounded-full transition-transform',
          colorClasses[color].background,
        )}
      />
      <span className="absolute top-1/2 left-1/2 h-80 w-full -translate-x-1/2 -translate-y-1/2 scale-0 overflow-hidden rounded-full bg-white transition-transform duration-400 group-hover/button:scale-100 group-disabled/button:scale-0!" />
      <span
        className={clsx(
          'relative z-1 flex items-center justify-center tracking-wider text-white uppercase transition-colors group-disabled/button:text-white!',
          colorClasses[color].content,
          sizeClasses[size],
        )}
      >
        {children}
      </span>
    </>
  );
}

export function Button({
  color,
  size,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonStyleProps & ComponentProps<'button'>) {
  return (
    <button type={type} className={clsx(baseClasses, className)} {...rest}>
      <ButtonLayers color={color} size={size}>
        {children}
      </ButtonLayers>
    </button>
  );
}

export function ButtonLink({
  color,
  size,
  className,
  children,
  ...rest
}: ButtonStyleProps & LinkComponentProps<'a'> & { children: ReactNode }) {
  return (
    <Link className={clsx(baseClasses, className)} {...rest}>
      <ButtonLayers color={color} size={size}>
        {children}
      </ButtonLayers>
    </Link>
  );
}
